import { BuildClass, Do, fsmData } from '../../../../universal';
import { _, React } from '../../../lib';
import { FormButton, FormButtonSet } from '../buttons';
import { FormType } from '../form';
import { CJSX } from '../meta-types';
export const getFlattenedTags = (rs) => _.chain(rs.props.tagging?.options)
    .flatMap(x => {
    if ('GroupName' in x) {
        return x.Tags.map(childTag => ({
            ...childTag,
            Colour: childTag.Colour ?? x.Colour,
        }));
    }
    return [x];
})
    .sortBy(x => x.DisplayName)
    .keyBy(x => x.ID)
    .value();
export const getTagColour = (tag) => hexToHsl(tag.Colour ?? '888888');
const hexToHsl = (hex) => {
    const rgb = hex.replace(/[^0-9a-f]/g, '');
    const normalizedRgb = rgb.padEnd(6, '0');
    const r = parseInt(normalizedRgb.slice(0, 2), 16) / 255;
    const g = parseInt(normalizedRgb.slice(2, 4), 16) / 255;
    const b = parseInt(normalizedRgb.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) {
            h = (g - b) / d + (g < b ? 6 : 0);
        }
        else if (max === g) {
            h = (b - r) / d + 2;
        }
        else {
            h = (r - g) / d + 4;
        }
        h /= 6;
    }
    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100),
    };
};
const buildHSL = (colour) => {
    const h = colour.h;
    const s = colour.s;
    const l = Math.max(0, Math.min(100, colour.l));
    return `hsl(${h},${s}%,${l}%)`;
};
const modifyBrightness = (colour, amount) => ({
    ...colour,
    l: Math.max(0, Math.min(100, colour.l + amount)),
});
export const TagIndicator = (props) => {
    const colourBase = getTagColour(props.tag);
    const colourTopGrad = modifyBrightness(colourBase, 5);
    const colourBottomGrad = modifyBrightness(colourBase, -5);
    const colourBorder = modifyBrightness(colourBase, -20);
    const colourShadow = modifyBrightness(colourBase, -20);
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { className: "inline-block h-4 pt-[2px] mx-1 align-middle" },
            React.createElement("span", { className: "inline-block h-[10px] w-[10px] rounded-full bg-neutral-500 align-top", style: {
                    background: `linear-gradient(to bottom, ${buildHSL(colourTopGrad)}, ${buildHSL(colourBottomGrad)})`,
                    borderColor: buildHSL(colourBorder),
                    boxShadow: `0 0 0 1px ${buildHSL(colourShadow)}`,
                }, title: props.tag.DisplayName })),
        React.createElement(CJSX, { cond: props.includeText },
            React.createElement(React.Fragment, null, props.tag.DisplayName))));
};
export const FormFieldNoteTags = (rs) => FormType.DropdownMultiStringKey({
    lbl: 'Tags',
    tooltip: 'Select one or more tags to help categorise the note. If left empty, the system will apply tentative tags for you to approve based on the content.',
    placeholder: '(Optional)',
    hidden: !rs.props.tagging,
    options: getTagComboboxOptions(rs.props.tagging?.options),
});
export const getTagComboboxOptions = (options) => {
    return fsmData(options, {
        filter: x => !(x.NonEditable ?? false),
        sort: x => ('GroupName' in x ? x.GroupName : x.DisplayName),
        map: x => {
            if ('GroupName' in x) {
                return {
                    value: null,
                    selectable: false,
                    text: x.GroupName,
                    options: fsmData(x.Tags, {
                        filter: y => !(y.NonEditable ?? false),
                        sort: y => y.DisplayName,
                        map: y => ({
                            value: y.ID,
                            text: y.DisplayName,
                            tooltip: y.Description,
                        }),
                    }),
                };
            }
            return {
                value: x.ID,
                text: x.DisplayName,
                tooltip: x.Description,
            };
        },
    });
};
export const EditNoteTagsMulti = (props) => {
    const rs = props.rs;
    const [loading, setLoading] = React.useState(false);
    const [msg, setMsg] = React.useState('');
    const tags = _.chain(props.selected)
        .map(x => x.Tags.map(t => ({ Tag: t, Note: x.ID })))
        .flatten()
        .groupBy(x => x.Tag)
        .mapValues(x => x.map(y => y.Note))
        .value();
    const [toggledExplicitly, setToggledExplicitly] = React.useState({});
    const isChecked = (tagID) => toggledExplicitly[tagID] !== undefined
        ? toggledExplicitly[tagID] === true
        : (tags[tagID] ?? []).length == props.selected.length;
    const isNotChecked = (tagID) => toggledExplicitly[tagID] !== undefined
        ? toggledExplicitly[tagID] === false
        : (tags[tagID] ?? []).length == 0;
    const isAmbiguous = (tagID) => toggledExplicitly[tagID] === undefined &&
        !isChecked(tagID) &&
        !isNotChecked(tagID);
    const toggleTag = (tagID) => {
        if (toggledExplicitly[tagID] !== undefined) {
            setToggledExplicitly(s => ({ ...s, [tagID]: !s[tagID] }));
        }
        else {
            setToggledExplicitly(s => ({ ...s, [tagID]: true }));
        }
    };
    const onSave = () => {
        setLoading(true);
        setMsg('');
        const additions = _.chain(toggledExplicitly)
            .map((v, k) => ({
            tagID: k,
            isChecked: v,
        }))
            .filter(x => x.isChecked)
            .flatMap(x => props.selected.map(note => ({
            noteID: note.ID,
            tagID: x.tagID,
        })))
            .value();
        const deletions = _.chain(toggledExplicitly)
            .map((v, k) => ({
            tagID: k,
            isChecked: v,
        }))
            .filter(x => !x.isChecked)
            .flatMap(x => props.selected.map(note => ({
            noteID: note.ID,
            tagID: x.tagID,
        })))
            .value();
        rs.props.tagging?.onUpdate?.({
            additions: additions,
            deletions: deletions,
            onComplete: successOrError => {
                setLoading(false);
                if (successOrError === true) {
                    props.flyout?.Close();
                }
                else {
                    setMsg(successOrError);
                }
            },
        });
    };
    const renderTag = (tag) => {
        const isAmbig = isAmbiguous(tag.ID);
        const isCheck = isChecked(tag.ID);
        const isNotCheck = isNotChecked(tag.ID);
        const ambiguousCount = (tags[tag.ID] ?? []).length;
        const totalCount = props.selected.length;
        const tooltip = isAmbig
            ? `Only some (${ambiguousCount} / ${totalCount}) of the selected notes currently have this tag. - ${tag.Description}`
            : tag.Description;
        return (React.createElement("li", { key: tag.ID, title: tooltip, className: BuildClass({
                'list-none cursor-pointer': true,
                'bg-green-300': isCheck,
                'bg-neutral-300': isAmbig,
                'hover:bg-yellow-100': !isCheck && !isAmbig,
            }), onClick: () => {
                toggleTag(tag.ID);
            } },
            React.createElement(CJSX, { cond: !isAmbig },
                React.createElement("img", { className: BuildClass({
                        'inline w-4 h-4 mr-1': true,
                        'p-1': isNotCheck,
                    }), src: Do(() => {
                        if (isCheck) {
                            return '/static/img/i8/material-outline-checkmark.svg';
                        }
                        if (isNotCheck) {
                            return '/static/img/i8/ios16-cross.svg';
                        }
                        return null;
                    }) })),
            React.createElement(CJSX, { cond: isAmbig },
                React.createElement("span", { className: "text-center w-4 mr-1 inline-block" }, "\u2022")),
            tag.DisplayName,
            React.createElement(CJSX, { cond: isAmbig },
                React.createElement("span", { className: "text-neutral-600 ml-2 text-xs" },
                    ambiguousCount,
                    " / ",
                    totalCount))));
    };
    return (React.createElement("div", { className: "tailwind-wrapper" },
        React.createElement("ul", null, fsmData(rs.props.tagging?.options, {
            filter: x => !(x.NonEditable ?? false),
            sort: x => ('GroupName' in x ? x.GroupName : x.DisplayName),
            map: x => {
                if ('GroupName' in x) {
                    return (React.createElement("li", { key: `group-${x.GroupName}`, className: BuildClass({
                            'list-none': true,
                        }) },
                        x.GroupName,
                        React.createElement("ul", null, fsmData(x.Tags, {
                            filter: y => !(y.NonEditable ?? false),
                            sort: y => y.DisplayName,
                            map: y => renderTag(y),
                        }))));
                }
                return renderTag(x);
            },
        })),
        React.createElement(FormButtonSet, { loading: loading, msg: msg },
            React.createElement(FormButton, { lbl: "Save", onClick: onSave }),
            React.createElement(FormButton, { lbl: "Cancel", onClick: () => {
                    props.flyout?.Close();
                } }))));
};
//# sourceMappingURL=tags.js.map